<template>
  <div>
    <Head title="Laporan Sebaran Kecamatan" />

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full p-4 font-semibold bg-gray-200">Filter</div>

      <div class="flex flex-wrap w-full p-6">
        <div class="w-full font-semibold lg:w-3/12">Periode Beasiswa:</div>

        <v-select
          v-model="form.periode"
          :options="periodes"
          class="w-full lg:w-9/12"
          :reduce="p => p.id"
          label="nama"
          placeholder="Pilih Periode"
        />
      </div>
    </div>

    <!-- TOTAL KOTA CILEGON -->
    <div class="w-full p-4 mb-6 bg-white border rounded shadow">
      <h2 class="mb-2 text-lg font-semibold">Total Kota Cilegon</h2>

      <p class="text-sm">
        Total Pemohon:
        <strong>{{ total_kota }}</strong>
      </p>

      <div class="flex flex-wrap gap-4 mt-2 text-sm">
        <span
          v-for="s in statusList"
          :key="s"
          class="px-3 py-1 text-white border rounded"
          :style="{ backgroundColor: statusColors[s] }"
        >
          {{ s }}: <strong>{{ status_kota[s] ?? 0 }}</strong>
        </span>
      </div>
    </div>

    <!-- TABEL -->
    <div class="mb-10 overflow-auto bg-white border rounded shadow">
      <div class="flex justify-end p-3 border-b bg-gray-50">
        <a
          :href="exportUrl"
          class="px-4 py-2 text-sm font-semibold text-white rounded shadow bg-green-600 hover:bg-green-700"
        >
          ⬇ Download Excel
        </a>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-100 border-b">
            <th class="p-2 text-left border">Kecamatan</th>
            <th class="p-2 text-center border">Total</th>

            <th
              v-for="s in statusList"
              :key="s"
              class="p-2 text-center border"
            >
              {{ s }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, i) in sortedReport"
            :key="i"
            class="border-b hover:bg-gray-50"
          >
            <!-- LINK KE HALAMAN KELURAHAN + FILTER PERIODE -->
            <td class="p-2 font-semibold border">
              <Link
                :href="route('report.kelurahan', {
                  kecamatan: item.kecamatan.replace('Kecamatan ', ''),
                  periode: form.periode
                })"
                class="text-indigo-600 hover:underline"
              >
                {{ item.kecamatan }}
              </Link>
            </td>

            <td class="p-2 text-center border">{{ item.total_applications }}</td>

            <td
              v-for="s in statusList"
              :key="s"
              class="p-2 font-semibold text-center border"
              :style="{ color: statusColors[s] }"
            >
              {{ item.status[s] ?? 0 }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-xl font-bold">Grafik Sebaran Status Universitas</h2>

      <apexchart
        width="100%"
        height="420"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>



<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'

export default {
  components: {
    Head,
    Link,
    apexchart: VueApexCharts,
  },

  layout: Layout,

  props: {
    report: Array,
    periodes: Array,
    filters: Object,
    total_kota: Number,
    status_kota: Object,
  },

  data() {
    return {
      form: {
        periode: parseInt(this.filters.periode) || null,
      },
    }
  },

  computed: {
    // WARNA SETIAP STATUS
    statusColors() {
      return {
        PTN: '#3b82f6',  // blue-500
        PTS: '#22c55e',  // green-500
        PTNL: '#8b5cf6', // violet-500
        PTKL: '#eab308', // yellow-500
      }
    },

    // urutkan kota cilegon ke atas
    sortedReport() {
      const cloned = [...this.report]
      const idx = cloned.findIndex(r => r.kecamatan === 'Kota Cilegon')

      if (idx > -1) {
        const item = cloned.splice(idx, 1)[0]
        cloned.unshift(item)
      }

      return cloned
    },

    exportUrl() {
      const params = new URLSearchParams()
      if (this.form.periode) params.set('periode', this.form.periode)
      return this.route('report.kecamatan.export') + '?' + params.toString()
    },

    statusList() {
      return Object.keys(this.report[0].status).filter(s => s !== '-')
    },

    kecamatanLabels() {
      return this.sortedReport.map(r => r.kecamatan)
    },

    chartSeries() {
      return this.statusList.map(status => ({
        name: status,
        data: this.sortedReport.map(r => r.status[status] ?? 0),
      }))
    },

    chartOptions() {
      return {
        chart: {
          stacked: false,
          toolbar: { show: false },
        },
        xaxis: {
          categories: this.kecamatanLabels,
          labels: { rotate: -45 },
        },
        yaxis: {
          min: 0,
        },
        dataLabels: { enabled: true },
        legend: { position: 'top' },

        // WARNA CHART
        colors: this.statusList.map(s => this.statusColors[s]),
      }
    },
  },

  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy(this.form)

        this.$inertia.get(
          this.route('report.kecamatan'),
          Object.keys(query).length ? query : { remember: 'forget' },
          {
            preserveState: true,
            preserveScroll: true,
          }
        )
      }, 150),
      deep: true,
    },
  },

  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
  },
}
</script>
